// src/pages/ModulesPage.jsx
import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import {
  ArrowLeft,
  BookOpen,
  Clock,
  Sparkles,
  Zap,
  Award,
  Target,
  TrendingUp,
  Heart,
  Users,
  Lightbulb,
  CheckCircle,
  Rocket
} from 'lucide-react';
import {
  modulesByLevel,
  getAvailableModules
} from '../data/modules';
import { useApp } from '../context/AppContext';
import EnhancedModuleCard from '../components/modules/EnhancedModuleCard';

const ModulesPage = () => {
  const navigate = useNavigate();
  const { moduleProgress } = useApp();
  const [selectedLevel, setSelectedLevel] = useState('all');

  // Get all modules
  const completedModuleIds = Object.keys(moduleProgress || {})
    .filter(id => moduleProgress[id].completed);

  const allModules = Object.values(modulesByLevel).flat();
  const availableModuleIds = getAvailableModules(completedModuleIds).map(m => m.id);

  // Filter by level if selected
  const displayModules = selectedLevel === 'all'
    ? allModules
    : allModules.filter(m => m.difficulty?.toLowerCase() === selectedLevel);

  // Helper function to determine module status
  const getModuleStatus = (module) => {
    if (completedModuleIds.includes(module.id)) return 'completed';
    if (!availableModuleIds.includes(module.id)) return 'locked';

    const moduleData = moduleProgress?.[module.id];
    if (moduleData?.started) return 'inProgress';

    return 'available';
  };

  // Calculate overall progress
  const totalModules = allModules.length;
  const completedCount = completedModuleIds.length;
  const progressPercentage = Math.round((completedCount / totalModules) * 100);

  return (
    <div className="min-h-screen bg-slate-950 pt-24 md:pt-40 pb-16">
      {/* Enhanced animated background with more visual interest */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-gradient-to-br from-indigo-500/15 to-purple-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-gradient-to-br from-violet-500/15 to-pink-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }} />
        <div className="absolute top-1/3 left-1/4 w-[400px] h-[400px] bg-gradient-to-br from-cyan-500/10 to-blue-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '4s' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[350px] h-[350px] bg-gradient-to-br from-amber-500/10 to-orange-500/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '6s' }} />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Back Button. Not on iOS: /modules is a tab's own screen there, so a
            history Back either does nothing or walks out of the tab. */}
        {!Capacitor.isNativePlatform() && (
          <button
            onClick={() => navigate(-1)}
            className="mb-6 text-slate-400 hover:text-white transition-colors flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        )}

        {/* Hero Section - Journey Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="max-w-5xl mx-auto">
            {/* Main Hero */}
            <div className="text-center mb-12">
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="inline-flex items-center gap-2 mb-6 px-4 py-2 bg-gradient-to-r from-indigo-500/10 to-violet-500/10 rounded-full border border-indigo-500/20"
              >
                <Rocket className="w-4 h-4 text-indigo-400" />
                <span className="text-indigo-300 text-sm font-medium">Your Learning Journey Begins</span>
              </motion.div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Think Like a
                <span className="block bg-gradient-to-r from-indigo-400 via-violet-400 to-sky-400 bg-clip-text text-transparent">
                  Philosopher
                </span>
              </h1>

              <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
                From holding opinions to building arguments. From accepting ideas to examining them. From asking what
                you think to knowing why you think it. Journey through twenty-six centuries of humanity's deepest questions in
                8 progressive modules: logic, knowledge, ethics, reality, mind, justice and meaning. The first three forge your instruments; the five after aim them at the questions that matter.
              </p>

              {/* Module Preview Carousel */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.3, duration: 0.6 }}
                className="mb-12 max-w-4xl mx-auto"
              >
                <div className="relative">
                  {/* Main featured modules showcase */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6">
                    {allModules.slice(0, 4).map((module, idx) => (
                      <motion.div
                        key={module.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 + idx * 0.1 }}
                        whileHover={{ scale: 1.05, y: -5 }}
                        className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-white/10 group cursor-pointer"
                        onClick={() => navigate(`/modules/${module.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/modules/${module.id}`);
                          }
                        }}
                        aria-label={`Open module: ${module.title}`}
                      >
                        <img
                          src={module.heroImage || module.image || `/images/cloud-${String(module.order || idx + 1).padStart(2, '0')}.webp`}
                          alt={module.title}
                          className="w-full aspect-square object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full aspect-square bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center';
                            fallback.innerHTML = `<div class="text-4xl">${module.emoji || '📚'}</div>`;
                            e.target.parentNode.appendChild(fallback);
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent group-hover:from-black group-hover:via-black/60 transition-colors duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-2.5">
                            <p className="text-white font-bold text-[11px] sm:text-xs leading-snug line-clamp-3">{module.title}</p>
                          </div>
                        </div>
                        {/* Module number badge */}
                        <div className="absolute top-2 left-2 w-6 h-6 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                          <span className="text-white text-xs font-bold">{idx + 1}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Second row */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                    {allModules.slice(4, 8).map((module, idx) => (
                      <motion.div
                        key={module.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.8 + idx * 0.1 }}
                        whileHover={{ scale: 1.05, y: -5 }}
                        className="relative rounded-xl overflow-hidden shadow-2xl border-2 border-white/10 group cursor-pointer"
                        onClick={() => navigate(`/modules/${module.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/modules/${module.id}`);
                          }
                        }}
                        aria-label={`Open module: ${module.title}`}
                      >
                        <img
                          src={module.heroImage || module.image || `/images/cloud-${String(module.order || idx + 5).padStart(2, '0')}.webp`}
                          alt={module.title}
                          className="w-full aspect-square object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            const fallback = document.createElement('div');
                            fallback.className = 'w-full aspect-square bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center';
                            fallback.innerHTML = `<div class="text-4xl">${module.emoji || '📚'}</div>`;
                            e.target.parentNode.appendChild(fallback);
                          }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-transparent group-hover:from-black group-hover:via-black/60 transition-colors duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-2.5">
                            <p className="text-white font-bold text-[11px] sm:text-xs leading-snug line-clamp-3">{module.title}</p>
                          </div>
                        </div>
                        {/* Module number badge */}
                        <div className="absolute top-2 left-2 w-6 h-6 bg-black/70 backdrop-blur-sm rounded-full flex items-center justify-center border border-white/20">
                          <span className="text-white text-xs font-bold">{idx + 5}</span>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  {/* Decorative glow overlay */}
                  <div className="absolute -inset-1 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 blur-xl -z-10 rounded-2xl" />
                </div>
              </motion.div>

              {/* Journey Highlights */}
              <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-emerald-500/15 to-teal-500/15 rounded-xl p-6 border border-emerald-500/40 hover:border-emerald-400/60 transition-all shadow-lg shadow-emerald-500/10"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg shadow-emerald-500/50">
                    <Target className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">Structured Path</h3>
                  <p className="text-slate-100 text-sm leading-relaxed">
                    Progressive modules that build on each other, taking you from first questions to confident philosophical thinking
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-gradient-to-br from-amber-500/15 to-orange-500/15 rounded-xl p-6 border border-amber-500/40 hover:border-amber-400/60 transition-all shadow-lg shadow-amber-500/10"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg shadow-amber-500/50">
                    <Lightbulb className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">Research-Grounded</h3>
                  <p className="text-slate-100 text-sm leading-relaxed">
                    Grounded in the real thing, Socrates to Sartre, with quotations and studies checked against their sources
                  </p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-gradient-to-br from-rose-500/15 to-pink-500/15 rounded-xl p-6 border border-rose-500/40 hover:border-rose-400/60 transition-all shadow-lg shadow-rose-500/10"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-rose-500 to-pink-500 rounded-xl flex items-center justify-center mb-4 mx-auto shadow-lg shadow-rose-500/50">
                    <Heart className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2">Interactive Tools</h3>
                  <p className="text-slate-100 text-sm leading-relaxed">
                    Practice tools and exercises you can apply to real questions immediately
                  </p>
                </motion.div>
              </div>
            </div>

            {/* What You'll Achieve */}
            <div className="bg-gradient-to-br from-amber-500/10 via-orange-500/10 to-yellow-500/10 backdrop-blur-sm rounded-2xl p-8 border border-amber-500/30 mb-12">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent mb-6 flex items-center gap-3">
                <Award className="w-6 h-6 text-amber-400" />
                What You'll Achieve
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {[
                  "Ask the questions that cut to the heart of any issue",
                  "Spot the logical fallacies hiding in arguments everywhere",
                  "Judge what's actually worth believing, and what isn't",
                  "Reason through moral dilemmas with three classic frameworks",
                  "Wrestle with free will, identity and the nature of reality",
                  "Explore consciousness and what minds actually are",
                  "Think clearly about justice, rights and how we live together",
                  "Build meaning deliberately, even in an absurd world"
                ].map((achievement, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 + idx * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                    <span className="text-white font-medium">{achievement}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            {/* How It Works */}
            <div className="bg-gradient-to-br from-purple-500/10 via-fuchsia-500/10 to-pink-500/10 rounded-2xl p-8 border border-purple-500/30 mb-12">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-6 flex items-center gap-3">
                <TrendingUp className="w-6 h-6 text-purple-400" />
                How Your Journey Works
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-fuchsia-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-purple-500/50">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Discover the Why</h3>
                    <p className="text-slate-200">Meet the questions, the thinkers, and the arguments that shaped each area of philosophy</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-fuchsia-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-fuchsia-500/50">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Master the Techniques</h3>
                    <p className="text-slate-200">Learn specific, actionable methods, from Socratic questioning to steelmanning the other side</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-pink-500/50">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Practice & Refine</h3>
                    <p className="text-slate-200">Use interactive tools that help you test, track and sharpen your reasoning</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-rose-500 to-orange-500 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-rose-500/50">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="text-white font-bold mb-1">Celebrate Progress</h3>
                    <p className="text-slate-200">Watch your awareness transform as you unlock each module and build fluency</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Progress Overview */}
            <div className="bg-gradient-to-br from-cyan-500/10 via-blue-500/10 to-indigo-500/10 backdrop-blur-sm rounded-xl p-6 border border-cyan-500/30">
              <div className="flex flex-wrap items-center justify-between gap-y-2 mb-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  <Users className="w-5 h-5 text-cyan-400" />
                  <span className="text-white font-bold">Your Progress</span>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
                    <span className="text-cyan-400 text-sm font-bold">
                      {progressPercentage}% Complete
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-medium">{totalModules} Modules</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-cyan-400" />
                    <span className="text-white font-medium">~9½ Hours</span>
                  </div>
                </div>
              </div>
              <div className="w-full bg-slate-800/50 rounded-full h-3 overflow-hidden border border-cyan-500/30">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 1, ease: "easeOut" }}
                  className="h-3 bg-gradient-to-r from-cyan-500 via-blue-500 to-indigo-500 rounded-full shadow-lg shadow-cyan-500/50"
                />
              </div>
            </div>
          </div>
        </motion.div>

        {/* Level Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {[
            { value: 'all', gradient: 'from-indigo-600 to-purple-600', border: 'border-indigo-500/50' },
            { value: 'foundation', gradient: 'from-emerald-700 to-teal-700', border: 'border-emerald-500/50' },
            { value: 'essential', gradient: 'from-amber-700 to-orange-700', border: 'border-amber-500/50' },
            { value: 'advanced', gradient: 'from-rose-700 to-pink-700', border: 'border-rose-500/50' }
          // A level with no modules (the curriculum has no "essential" tier)
          // would only lead to an empty list, so its button is not shown.
          ].filter(({ value }) => value === 'all' || (modulesByLevel[value] || []).length > 0).map(({ value, gradient, border }) => (
            <button
              key={value}
              onClick={() => setSelectedLevel(value)}
              aria-pressed={selectedLevel === value}
              className={`px-5 py-2.5 rounded-full text-sm font-bold transition-all duration-200 ${
                selectedLevel === value
                  ? `bg-gradient-to-r ${gradient} text-white shadow-lg shadow-${gradient.split('-')[1]}-500/30 scale-105`
                  : `bg-slate-800/50 text-slate-300 hover:bg-slate-700 border ${border} hover:scale-105`
              }`}
            >
              {value.charAt(0).toUpperCase() + value.slice(1)}
            </button>
          ))}
        </div>

        {/* Divider with CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="max-w-5xl mx-auto mb-16"
        >
          <div className="py-8">
            <div className="flex justify-center">
              {/* No animate-pulse: an opacity cycle on text drops it below 4.5:1
                  for most of every cycle (fleet lesson from the DDDating audit). */}
              <span className="px-8 py-3 bg-gradient-to-r from-indigo-700 via-purple-700 to-pink-700 text-white rounded-full text-base font-bold shadow-2xl shadow-indigo-500/50">
                ↓ Begin Your Transformation Below ↓
              </span>
            </div>
          </div>
        </motion.div>

        {/* Module Grid Section */}
        <div className="mb-12">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h2 className="text-4xl font-bold bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">
                {selectedLevel === 'all' ? 'Your Learning Modules' : `${selectedLevel.charAt(0).toUpperCase() + selectedLevel.slice(1)} Modules`}
              </h2>
              <p className="text-slate-100 text-lg font-medium">
                {selectedLevel === 'all'
                  ? 'Choose a module below to start your journey'
                  : `Focussed on ${selectedLevel}-level philosophical thinking`
                }
              </p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500/20 to-amber-500/20 rounded-full border border-yellow-500/30">
              <Sparkles className="w-5 h-5 text-yellow-400" />
              <span className="text-yellow-300 font-semibold">Interactive Learning</span>
            </div>
          </div>

          {/* Responsive Module Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {displayModules.map((module, index) => {
              const moduleStatus = getModuleStatus(module);
              return (
                <EnhancedModuleCard
                  key={module.id}
                  module={module}
                  status={moduleStatus}
                  moduleIndex={index}
                  onClick={() => moduleStatus !== 'locked' && navigate(
                    `/modules/${module.id}`,
                    // A finished module opens on its overview: "Review" means
                    // see what it covered. Everything else resumes where you were.
                    moduleStatus === 'completed' ? { state: { overview: true } } : undefined
                  )}
                />
              );
            })}
          </div>

          {displayModules.length === 0 && (
            <div className="bg-slate-900/50 backdrop-blur-sm rounded-xl p-8 text-center border border-slate-800">
              <p className="text-slate-400">
                {selectedLevel === 'all'
                  ? 'No modules available. Complete prerequisite modules to unlock more.'
                  : `No ${selectedLevel} modules available.`
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModulesPage;
